import type { LoaderFunctionArgs } from '~/types/route-args';
import { loadElasticSearchResults } from '@modules/professional/api/load-elastic-search-results.server';
import { getProjectsFromESSearchResults } from '@modules-rocco/search/data-mappers/tile-project';
import type { DetailTile } from '~/ui-rocco/pages/detail-shared';

/** Resource-route path for a practice's projects, as the member overlay lists them. */
export const PROFESSIONAL_PROJECTS_ROUTE = '/remix-api/professional-projects';

/** Two rows of the four-up grid — the overlay lists, it does not page. */
const PAGE_SIZE = 8;

export interface ProfessionalProjectsResponse {
    tiles: DetailTile[];
}

/**
 * A practice's projects, for the member overlay's closing grid.
 *
 * Fetched on open rather than loaded with the page: the overlay is opened from
 * one tile out of a carousel, and loading every member's practice up front
 * would be a search per face on a page that mostly never opens one.
 *
 * The same search the practice's own Projects tab runs, so the Search Service
 * has usually answered it before.
 */
export const loader = async ({ request, context }: LoaderFunctionArgs) => {
    const urlSegment = new URL(request.url).searchParams.get('urlSegment');
    if (!urlSegment) {
        return Response.json({
            tiles: [],
        } satisfies ProfessionalProjectsResponse);
    }

    try {
        const esData = await loadElasticSearchResults({
            context,
            request,
            urlSegment,
            searchType: 'project',
            size: PAGE_SIZE,
        });

        const tiles = getProjectsFromESSearchResults(
            esData.searchResult,
            context.user,
        ).map(
            (project): DetailTile => ({
                id: project.id,
                link: project.link,
                title: project.title,
                images: project.images,
                pinType: 'ProjectPin',
                professional: {
                    id: project.professional.id,
                    name: project.professional.name,
                },
                professionalLink: project.professional.link,
            }),
        );

        return Response.json({ tiles } satisfies ProfessionalProjectsResponse);
    } catch (error) {
        // The grid is a tail-end extra: an overlay that cannot list the
        // practice's work still books a meeting and sends a message. The
        // reader is told nothing, so the failure has to reach the log or a
        // search that has stopped answering looks like a practice with no
        // projects.
        context.logger.error(`Error in ${PROFESSIONAL_PROJECTS_ROUTE}`, {
            error,
            urlSegment,
        });
        return Response.json({
            tiles: [],
        } satisfies ProfessionalProjectsResponse);
    }
};
